import React, { CSSProperties } from 'react';
import SideNavContent from './sideNavContent';
import { CustomScrollbar } from '../../components/CustomScrollbar';

export type layoutSideNavType = {
  sidebarStyle: CSSProperties;
  logoIconStyle: CSSProperties;
  logoTextStyle: CSSProperties;
  logoIconSrc: string;
  logoTextSrc: string;
  isCollapsed: boolean;
};

const LayoutSideNav: React.FC<layoutSideNavType> = ({
  sidebarStyle,
  logoIconSrc,
  logoTextSrc,
  logoIconStyle,
  logoTextStyle,
  isCollapsed,
}) => {
  return (
    <div
      className="fixed top-0 left-0 flex flex-col h-screen px-4 py-2 bg-white transition-all duration-300 overflow-hidden"
      style={{ ...sidebarStyle, zIndex: 50 }}
    >
      {/* Logo — always pinned at the top, never scrolls. No extra paddingLeft:
          the icon's ~14px inset from the row edge (vs. the nav items' 16px)
          is baked into Figma's flattened logo artwork, not a CSS offset —
          adding padding here shifts the icon+text row as a unit and clips
          the text against the row's overflow-hidden edge instead. */}
      <div className="flex items-center gap-[5px] w-full shrink-0 overflow-hidden">
        <img
          src={logoIconSrc}
          alt="pozytron logo"
          className="shrink-0"
          style={logoIconStyle}
        />
        {/* Same fade fix as the bottom illustration below: stays mounted,
            opacity-transitions instead of being conditionally unmounted
            (which removed it instantly on collapse, before the rail even
            started narrowing). */}
        <img
          src={logoTextSrc}
          alt="pozytron"
          className="shrink-0 transition-opacity duration-300"
          style={{ ...logoTextStyle, opacity: isCollapsed ? 0 : 1 }}
        />
      </div>

      {/* Nav items + illustration share one scroll region — the illustration
          is the list's last item, not a fixed sibling outside it, so at
          normal heights both are simply visible with no scroll needed, but
          when space runs short (e.g. the page zoomed in) the pair scrolls
          together instead of the illustration acting as a fixed boundary
          the list runs into. Its own size never changes either way. */}
      <div className="flex flex-1 min-h-0 flex-col mt-[37px]">
        <CustomScrollbar className="flex-1 min-h-0" thumbHeight={150}>
          {/* min-h-full + the illustration's mt-auto is what actually pins
              it to the bottom — without this wrapper, a role with few nav
              items (e.g. Client, Admin) leaves the illustration sitting
              right after the short list instead of at the sidebar's true
              bottom, since nothing here previously reserved the leftover
              space. mt-auto only pushes when there's slack to consume; once
              the items alone exceed the available height (e.g. Super Admin)
              there's no slack left and the pair overflows/scrolls exactly
              as before. */}
          <div className="flex flex-col min-h-full">
            <SideNavContent isIconOnly={isCollapsed} />

            {/* Fades with opacity (matching the rail's own 300ms width
                transition) instead of being conditionally unmounted —
                unmounting removed it on the first frame, before the rail had
                visibly started narrowing, so it looked like it vanished
                abruptly on collapse rather than shrinking away with the
                sidebar. */}
            <img
              src="/navBarIllastration.svg"
              alt="pozytron logo"
              className="block shrink-0 mt-auto transition-opacity duration-300"
              style={{ opacity: isCollapsed ? 0 : 1 }}
            />
          </div>
        </CustomScrollbar>
      </div>
    </div>
  );
};

export default LayoutSideNav;
